import {DoubleString} from '@raison/types';
import BigNumber from 'bignumber.js';

// could have written it with a Proxy but TS would be oblivious and angry
export class Amount<T> {
    private readonly innerValue: BigNumber;

    constructor(
        value: Amount.Value<T>,
        private readonly unit: T,
        private readonly isSameUnit: (unitA: T, unitB: T) => boolean
    ) {
        this.innerValue = this.toInnerValue(value);
    }

    isOfSameUnitAs(amount: Amount<any>): amount is Amount<T> {
        return this.isSameUnit(this.unit, amount.unit);
    }

    isOfUnit(someUnit: any): someUnit is T {
        return this.isSameUnit(this.unit, someUnit);
    }

    add(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.cloneWithValue(this.innerValue.plus(this.toInnerValue(value)));
    }

    subtract(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.cloneWithValue(this.innerValue.minus(this.toInnerValue(value)));
    }

    multiply(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.cloneWithValue(this.innerValue.times(this.toInnerValue(value)));
    }

    divide(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.cloneWithValue(this.innerValue.div(this.toInnerValue(value)));
    }

    lt(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.innerValue.lt(this.toInnerValue(value));
    }

    lte(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.innerValue.lte(this.toInnerValue(value));
    }

    gt(value: Amount.Value<T>) {
        return !this.lte(value);
    }

    gte(value: Amount.Value<T>) {
        return !this.lt(value);
    }

    eq(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.innerValue.eq(this.toInnerValue(value));
    }

    compare(value: Amount.Value<T>) {
        this.assertSameness(value);
        return this.innerValue.comparedTo(this.toInnerValue(value));
    }

    toNumber() {
        return this.innerValue.toNumber();
    }

    toUnit() {
        return this.unit;
    }

    private toInnerValue(value: Amount.Value<T>) {
        if (value instanceof Amount) {
            return value.innerValue;
        } else {
            return new BigNumber(value);
        }
    }

    private assertSameness(value: Amount.Value<T>) {
        if (value instanceof Amount && !this.isSameUnit(this.unit, value.unit)) {
            throw new TypeError(`Can't operate on amounts of different unit`);
        }
    }

    private cloneWithValue(innerValue: BigNumber): Amount<T> {
        return Object.setPrototypeOf({...this, innerValue}, Amount.prototype);
    }
}

export namespace Amount {
    export type Value<U, Self = Amount<U>> = DoubleString | number | Self;
}


